import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Card, Field, Input, Spinner } from '../components/ui';
import { Notice, Page, PageHeader, Section, Stack } from '../components/Page';
import type { OrganisationBranding } from '../lib/types';

/**
 * The account's own identity — the name and mark every respondent sees above a form.
 *
 * This is one profile for the whole account rather than one per form. A logo pasted into
 * twelve themes is a logo that is wrong in eleven of them the day it changes, and the
 * schema always intended it this way: `Tenant.BrandingJson` has existed since the first
 * migration with nothing ever reading or writing it.
 */
export function Organisation() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<OrganisationBranding>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const organisation = useQuery({
    queryKey: ['organisation'],
    queryFn: () => api.organisation.get(),
  });

  // Seeded once the server answers. Editing before that would be typing into a form that
  // is about to be overwritten.
  useEffect(() => {
    if (organisation.data) setDraft(organisation.data.branding ?? {});
  }, [organisation.data]);

  const save = useMutation({
    mutationFn: () => api.organisation.update(draft),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['organisation'] });
    },
    onError: (e: Error) => { setSaved(false); setError(e.message); },
  });

  if (organisation.isLoading) return <div className="p-8"><Spinner /></div>;

  const account = organisation.data;
  const set = (patch: Partial<OrganisationBranding>) => {
    setSaved(false);
    setDraft({ ...draft, ...patch });
  };

  return (
    <Page>
      <PageHeader
        icon="building"
        title="Organisation"
        description="Your account's name and mark. Forms inherit these, so a respondent
                     sees who is asking before they answer anything."
      />

      <Stack>
        {error && <Notice tone="danger">{error}</Notice>}
        {saved && <Notice tone="success">Saved. New responses will see this straight away.</Notice>}

        <Section
          title="Identity"
          hint="Shown above every hosted form. Leave a field empty and the form simply
                does not draw it."
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Display name"
              hint={`Defaults to “${account?.name ?? ''}”, this account's name.`}
            >
              <Input
                value={draft.displayName ?? ''}
                placeholder={account?.name}
                onChange={(e) => set({ displayName: e.target.value })}
              />
            </Field>

            <Field
              label="Logo URL"
              hint="Must be an https address. The image is loaded by every respondent's
                    browser, so an http one would put a security warning on the page."
            >
              <Input
                type="url"
                value={draft.logoUrl ?? ''}
                placeholder="https://example.com/logo.svg"
                onChange={(e) => set({ logoUrl: e.target.value })}
              />
            </Field>

            <Field label="Support email" hint="Where a respondent writes if something breaks.">
              <Input
                type="email"
                value={draft.supportEmail ?? ''}
                placeholder="support@example.com"
                onChange={(e) => set({ supportEmail: e.target.value })}
              />
            </Field>

            <Field label="Website">
              <Input
                type="url"
                value={draft.websiteUrl ?? ''}
                placeholder="https://example.com"
                onChange={(e) => set({ websiteUrl: e.target.value })}
              />
            </Field>

            <div className="sm:col-span-2">
              <Field
                label="Footer line"
                hint="One line under the form — a legal entity, a charity number, a privacy note."
              >
                <Input
                  value={draft.footerText ?? ''}
                  placeholder="Acme Research Ltd · Registered in England 01234567"
                  onChange={(e) => set({ footerText: e.target.value })}
                />
              </Field>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => { setDraft(account?.branding ?? {}); setSaved(false); }}
            >
              Reset
            </Button>
          </div>
        </Section>

        <Section
          title="What a respondent sees"
          hint="The header of every hosted form, drawn from what is above."
        >
          <Preview branding={draft} fallbackName={account?.name ?? ''} />
        </Section>
      </Stack>
    </Page>
  );
}

/**
 * The header as the hosted page will render it.
 *
 * Worth showing because the fields are individually unremarkable and the result is the
 * first thing a respondent judges the form by. It also makes a broken logo URL obvious
 * here rather than on somebody else's screen.
 */
function Preview({
  branding, fallbackName,
}: { branding: OrganisationBranding; fallbackName: string }) {
  const name = branding.displayName?.trim() || fallbackName;
  const logo = branding.logoUrl?.trim();
  const footer = branding.footerText?.trim();

  return (
    <Card className="bg-[var(--ff-surface-sunken)] p-5">
      <div className="mx-auto max-w-md rounded-lg border border-[var(--ff-border)]
                      bg-[var(--ff-surface)] p-5">
        <div className="mb-3 flex items-center justify-center">
          {logo ? (
            <img
              src={logo}
              alt={name}
              className="max-h-12 max-w-[60%] object-contain"
              // A URL that does not resolve leaves a broken-image glyph, which reads as a
              // bug in the studio rather than as a wrong address.
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
          ) : (
            <span className="text-sm font-semibold">{name}</span>
          )}
        </div>

        <div className="space-y-2" aria-hidden="true">
          <div className="h-3 w-2/3 rounded bg-[var(--ff-surface-sunken)]" />
          <div className="h-8 rounded border border-[var(--ff-border)]" />
          <div className="h-3 w-1/2 rounded bg-[var(--ff-surface-sunken)]" />
          <div className="h-8 rounded border border-[var(--ff-border)]" />
        </div>

        {footer && (
          <p className="mt-4 border-t border-[var(--ff-border)] pt-2 text-center text-[11px]
                        text-[var(--ff-text-muted)]">
            {footer}
          </p>
        )}
      </div>

      <p className="mt-3 text-center text-[11px] text-[var(--ff-text-muted)]">
        The questions are a sketch. The header and footer are what actually renders.
      </p>
    </Card>
  );
}
